import { useEffect, useState } from 'preact/hooks';
import {
  platformBilling,
  Plan,
  PlatformStripeConfigSummary,
  CreatePlanInput,
  formatMoney,
  bpsToPercent,
} from './billingApi';
import { StepUpModal, StepUpRequiredError, runWithStepUp } from './StepUpModal';

type Tab = 'plans' | 'stripe';

export function BillingPage() {
  const [tab, setTab] = useState<Tab>('plans');
  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Billing</h1>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Global plan catalog and platform Stripe account. Per-org billing lives
        on each organization's detail page under the Billing tab.
      </p>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--admin-border)', marginBottom: 16 }}>
        {[
          { key: 'plans' as Tab, label: 'Plans' },
          { key: 'stripe' as Tab, label: 'Platform Stripe' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--admin-accent)' : '2px solid transparent',
              borderRadius: 0,
              padding: '8px 16px',
              color: tab === t.key ? 'var(--admin-text)' : 'var(--admin-text-dim)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'plans' && <PlansTab />}
      {tab === 'stripe' && <StripeConfigTab />}
    </div>
  );
}

// ─── Plans ───

function PlansTab() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);

  async function load() {
    setErr(null);
    try {
      const r = await platformBilling.listPlans(includeArchived);
      setPlans(r.plans || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  async function runAction(fn: () => Promise<void>) {
    try {
      await runWithStepUp(fn);
      await load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => runAction(fn));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived((e.target as HTMLInputElement).checked)}
          />
          <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>Include archived</span>
        </label>
        <button class="primary" onClick={() => setShowCreate(true)} style={{ marginLeft: 'auto' }}>
          + New plan
        </button>
      </div>
      {err && <div class="error-banner">{err}</div>}
      {info && (
        <div
          style={{
            background: 'rgba(79,209,139,0.15)',
            border: '1px solid var(--admin-success)',
            color: '#c2f2d6',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {info}
        </div>
      )}

      {plans.length === 0 ? (
        <p style={{ color: 'var(--admin-text-dim)' }}>
          No plans yet. Create a first plan — typically "Complimentary" ($0/mo, 0% commission) for internal / partner orgs.
        </p>
      ) : (
        <table class="admin-table">
          <thead>
            <tr>
              <th>Slug · v</th>
              <th>Name</th>
              <th>Interval</th>
              <th style={{ textAlign: 'right' }}>Recurring</th>
              <th style={{ textAlign: 'right' }}>Commission</th>
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id}>
                <td class="mono">
                  {p.slug}
                  <span style={{ color: 'var(--admin-text-dim)' }}>· v{p.version}</span>
                </td>
                <td>{p.name}</td>
                <td class="mono">{p.billingInterval}</td>
                <td style={{ textAlign: 'right' }} class="mono">
                  {formatMoney(p.recurringFeeCents, p.currency)}
                </td>
                <td style={{ textAlign: 'right' }} class="mono">
                  {bpsToPercent(p.commissionBps)}
                </td>
                <td>
                  <span
                    class={`badge ${p.status === 'active' ? 'success' : p.status === 'archived' ? 'failure' : ''}`}
                    style={
                      p.status === 'draft'
                        ? { background: 'var(--admin-surface-raised)', color: 'var(--admin-text-dim)' }
                        : undefined
                    }
                  >
                    {p.status}
                  </span>
                </td>
                <td class="mono">{new Date(p.createdAt).toLocaleDateString()}</td>
                <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {p.status === 'draft' && (
                    <button
                      onClick={() =>
                        runAction(async () => {
                          await platformBilling.activatePlan(p.id);
                          setInfo(`${p.slug} v${p.version} activated.`);
                        })
                      }
                    >
                      Activate
                    </button>
                  )}
                  {p.status !== 'archived' && (
                    <button
                      onClick={() =>
                        runAction(async () => {
                          if (!window.confirm(`Archive ${p.slug} v${p.version}?`)) return;
                          await platformBilling.archivePlan(p.id);
                          setInfo(`${p.slug} v${p.version} archived.`);
                        })
                      }
                    >
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            setInfo('Plan created.');
            load();
          }}
          onStepUp={(retry) => {
            setShowCreate(false);
            setStepUpFor(() => retry);
          }}
        />
      )}

      {stepUpFor && (
        <StepUpModal
          prompt="Plan changes require password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </div>
  );
}

function CreatePlanModal({
  onClose,
  onCreated,
  onStepUp,
}: {
  onClose(): void;
  onCreated(): void;
  onStepUp(retry: () => void): void;
}) {
  const [f, setF] = useState<CreatePlanInput>({
    slug: '',
    name: '',
    description: '',
    currency: 'USD',
    billingInterval: 'month',
    recurringFeeCents: 0,
    commissionBps: 0,
    paymentTermsDays: 0,
    dunningWarningAtDays: 7,
    dunningRestrictAtDays: 14,
    dunningSuspendAtDays: 30,
    dunningOffboardNoticeDays: 60,
    dunningOffboardEligibleDays: 90,
    activate: true,
  });
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    const create = async () => {
      await platformBilling.createPlan(f);
      onCreated();
    };
    try {
      await runWithStepUp(create);
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        onStepUp(async () => {
          try {
            await runWithStepUp(create);
          } catch (err) {
            setErr(err instanceof Error ? err.message : 'Unknown error');
            setSubmitting(false);
          }
        });
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="New plan" onClose={onClose}>
      {err && <div class="error-banner">{err}</div>}
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div class="field">
            <label>Slug</label>
            <input
              required
              placeholder="complimentary"
              value={f.slug}
              onInput={(e) => setF({ ...f, slug: (e.target as HTMLInputElement).value })}
            />
          </div>
          <div class="field">
            <label>Display name</label>
            <input
              required
              placeholder="Complimentary"
              value={f.name}
              onInput={(e) => setF({ ...f, name: (e.target as HTMLInputElement).value })}
            />
          </div>
        </div>
        <div class="field">
          <label>Description</label>
          <input
            value={f.description || ''}
            onInput={(e) => setF({ ...f, description: (e.target as HTMLInputElement).value })}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div class="field">
            <label>Currency</label>
            <input
              value={f.currency || 'USD'}
              onInput={(e) => setF({ ...f, currency: (e.target as HTMLInputElement).value })}
            />
          </div>
          <div class="field">
            <label>Interval</label>
            <select
              value={f.billingInterval || 'month'}
              onChange={(e) => setF({ ...f, billingInterval: (e.target as HTMLSelectElement).value })}
            >
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </div>
          <div class="field">
            <label>Trial days</label>
            <input
              type="number"
              value={String(f.trialDays || 0)}
              onInput={(e) => setF({ ...f, trialDays: Number((e.target as HTMLInputElement).value) })}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div class="field">
            <label>Recurring fee (cents)</label>
            <input
              type="number"
              value={String(f.recurringFeeCents || 0)}
              onInput={(e) =>
                setF({ ...f, recurringFeeCents: Number((e.target as HTMLInputElement).value) })
              }
            />
          </div>
          <div class="field">
            <label>Commission (bps — 500 = 5%)</label>
            <input
              type="number"
              value={String(f.commissionBps || 0)}
              onInput={(e) =>
                setF({ ...f, commissionBps: Number((e.target as HTMLInputElement).value) })
              }
            />
          </div>
        </div>
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--admin-text-dim)' }}>
            Dunning thresholds
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 8 }}>
            {(
              [
                ['dunningWarningAtDays', 'Warning'],
                ['dunningRestrictAtDays', 'Restrict'],
                ['dunningSuspendAtDays', 'Suspend'],
                ['dunningOffboardNoticeDays', 'Notice'],
                ['dunningOffboardEligibleDays', 'Eligible'],
              ] as Array<[keyof CreatePlanInput, string]>
            ).map(([key, label]) => (
              <div class="field" key={key}>
                <label>{label}</label>
                <input
                  type="number"
                  value={String((f[key] as number) || 0)}
                  onInput={(e) =>
                    setF({ ...f, [key]: Number((e.target as HTMLInputElement).value) })
                  }
                />
              </div>
            ))}
          </div>
        </details>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <input
            type="checkbox"
            checked={!!f.activate}
            onChange={(e) => setF({ ...f, activate: (e.target as HTMLInputElement).checked })}
          />
          <span>Activate on creation (skip draft)</span>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button class="primary" type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create plan'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Stripe Config ───

function StripeConfigTab() {
  const [config, setConfig] = useState<PlatformStripeConfigSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showConfigure, setShowConfigure] = useState(false);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);
  const [verifying, setVerifying] = useState(false);

  async function load() {
    setErr(null);
    try {
      const r = await platformBilling.getStripeConfig();
      setConfig(r.config);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function verify() {
    setErr(null);
    setInfo(null);
    setVerifying(true);
    try {
      await runWithStepUp(async () => {
        const r = await platformBilling.verifyStripe();
        setConfig(r.config);
        if (r.ok) setInfo('Stripe credentials verified.');
        else setErr(`Verification failed: ${r.error || 'unknown'}`);
      });
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => verify());
        setVerifying(false);
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        The MomentPix-owned Stripe account used to bill tenant organizations.
        Distinct from each org's consumer-facing Stripe (managed under their
        Gateway tab). Secrets are AEAD-sealed at rest.
      </p>
      {err && <div class="error-banner">{err}</div>}
      {info && (
        <div
          style={{
            background: 'rgba(79,209,139,0.15)',
            border: '1px solid var(--admin-success)',
            color: '#c2f2d6',
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {info}
        </div>
      )}

      {config && !config.configured && (
        <div
          style={{
            padding: 16,
            background: 'var(--admin-surface)',
            border: '1px solid var(--admin-border)',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <p>No platform Stripe account configured yet.</p>
          <button class="primary" onClick={() => setShowConfigure(true)}>
            Configure platform Stripe
          </button>
        </div>
      )}

      {config && config.configured && (
        <table class="admin-table">
          <tbody>
            <tr>
              <td style={{ width: 200, color: 'var(--admin-text-dim)' }}>Mode</td>
              <td class="mono">{config.mode}</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--admin-text-dim)' }}>Active</td>
              <td>
                <span class={`badge ${config.active ? 'success' : 'failure'}`}>
                  {config.active ? 'active' : 'inactive'}
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ color: 'var(--admin-text-dim)' }}>Publishable key</td>
              <td class="mono">{config.publishableKey.slice(0, 18)}…</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--admin-text-dim)' }}>Account ID</td>
              <td class="mono">{config.accountId || '—'}</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--admin-text-dim)' }}>Statement descriptor</td>
              <td class="mono">{config.statementDescriptor}</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--admin-text-dim)' }}>Last verified</td>
              <td class="mono">
                {config.lastVerifiedAt
                  ? new Date(config.lastVerifiedAt).toLocaleString()
                  : 'never'}
              </td>
            </tr>
            {config.verifyError && (
              <tr>
                <td style={{ color: 'var(--admin-danger)' }}>Verify error</td>
                <td style={{ color: 'var(--admin-danger)' }}>{config.verifyError}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {config && config.configured && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={verify} disabled={verifying}>
            {verifying ? 'Verifying…' : 'Verify credentials'}
          </button>
          <button onClick={() => setShowConfigure(true)}>Re-configure</button>
        </div>
      )}

      {showConfigure && (
        <ConfigureStripeModal
          current={config}
          onClose={() => setShowConfigure(false)}
          onSaved={() => {
            setShowConfigure(false);
            setInfo('Saved. Credentials remain inactive until verified — click Verify credentials.');
            load();
          }}
          onStepUp={(retry) => {
            setShowConfigure(false);
            setStepUpFor(() => retry);
          }}
        />
      )}

      {stepUpFor && (
        <StepUpModal
          prompt="Stripe configuration requires password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </div>
  );
}

function ConfigureStripeModal({
  current,
  onClose,
  onSaved,
  onStepUp,
}: {
  current: PlatformStripeConfigSummary | null;
  onClose(): void;
  onSaved(): void;
  onStepUp(retry: () => void): void;
}) {
  const [f, setF] = useState({
    mode: current?.mode || 'test',
    publishableKey: current?.publishableKey || '',
    secretKey: '',
    webhookSecret: '',
    statementDescriptor: current?.statementDescriptor || 'MOMENTPIX',
  });
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    const save = async () => {
      await platformBilling.configureStripe({
        mode: f.mode,
        publishableKey: f.publishableKey,
        secretKey: f.secretKey,
        webhookSecret: f.webhookSecret,
        statementDescriptor: f.statementDescriptor,
      });
      onSaved();
    };
    try {
      await runWithStepUp(save);
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        onStepUp(async () => {
          try {
            await runWithStepUp(save);
          } catch (err) {
            setErr(err instanceof Error ? err.message : 'Unknown error');
            setSubmitting(false);
          }
        });
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Configure platform Stripe" onClose={onClose}>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Secrets are AEAD-sealed before storage. Save first, then click Verify to
        activate. The statement descriptor appears on customers' card statements
        (22 char max).
      </p>
      {err && <div class="error-banner">{err}</div>}
      <form onSubmit={submit}>
        <div class="field">
          <label>Mode</label>
          <select value={f.mode} onChange={(e) => setF({ ...f, mode: (e.target as HTMLSelectElement).value })}>
            <option value="test">Test</option>
            <option value="live">Live</option>
          </select>
        </div>
        <div class="field">
          <label>Publishable key (pk_test_... or pk_live_...)</label>
          <input
            required
            value={f.publishableKey}
            onInput={(e) => setF({ ...f, publishableKey: (e.target as HTMLInputElement).value })}
          />
        </div>
        <div class="field">
          <label>Secret key (sk_test_... or sk_live_...)</label>
          <input
            required
            type="password"
            autoComplete="off"
            value={f.secretKey}
            onInput={(e) => setF({ ...f, secretKey: (e.target as HTMLInputElement).value })}
          />
        </div>
        <div class="field">
          <label>Webhook signing secret (whsec_...)</label>
          <input
            required
            type="password"
            autoComplete="off"
            value={f.webhookSecret}
            onInput={(e) => setF({ ...f, webhookSecret: (e.target as HTMLInputElement).value })}
          />
        </div>
        <div class="field">
          <label>Statement descriptor</label>
          <input
            maxLength={22}
            value={f.statementDescriptor}
            onInput={(e) => setF({ ...f, statementDescriptor: (e.target as HTMLInputElement).value })}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button class="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Shared modal ───

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose(): void;
  children: any;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 600,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}
